import React, { useCallback, useState } from "react"
import { EditGroup } from "../../Components/EditGroup"
import { BlockField, BlockRow } from "../../Components/Block"
import { ConfirmDialog } from "../../Components/ConfirmDialog"
import { Menu, MenuItemDividerProps } from "../../Components/Menu"
import { IShipUserCrewRole, IShipUserLevel } from "../../Types"
import { IShipUserStatus } from "../../Types/Generated/LogbookModel/ShipUser"
import { ShipDataViewData } from "../ShipData/Context"
import { userEditableFields } from "./Data"
import { useShipUserEdit } from "./useShipUserEdit"
import { InviteUserOverlay } from "./InviteUserOverlay"
import { ShipUserEditViewProps } from "./Params"

// ---------------------------------------------------------------------------
// Ship users with access to the app
// ---------------------------------------------------------------------------

type PendingAction = "deactivate" | "remove" | undefined;

export function ShipUserEditView({ editable, shipUserCrewRoleOptions, shipUserLevelOptions, showLabels = false, user, ship }: ShipUserEditViewProps) {
  const { app, t, edited, name, shortname, onEdited, updateShipUser, updateShipUserActive, removeShipUser } = useShipUserEdit({ user });
  const [showInviteOverlay, setShowInviteOverlay] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(undefined);
  const [crewRole, setCrewRole] = useState(user.shipUserCrewRole);

  const handleCrewRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = parseInt(e.target.value);
    setCrewRole(newRole);
    onEdited(e);
  };

  const cancelPendingAction = useCallback(() => setPendingAction(undefined), []);

  const confirmPendingAction = useCallback(async () => {
    if (pendingAction === "deactivate") {
      await updateShipUserActive(false);
    } else if (pendingAction === "remove") {
      await removeShipUser();
    }
    setPendingAction(undefined);
  }, [pendingAction, updateShipUserActive, removeShipUser]);

  // `status` is a client-only virtual field (@typegen-field): the server never sends it,
  // so any real ship-user loaded from the wire has `status: undefined`. Derive it here
  // instead of trusting the incoming field, so the roster shows a real "Invited" vs
  // "Active" badge.
  const status = user.active > 0 ? IShipUserStatus.active : IShipUserStatus.invited;
  const isPrimarySelf = user.shipUserLevel === IShipUserLevel.primary && user.userId === app.user?.id;
  // #1025 (tracker #827): reactivating a row whose `userId` link was cleared (by a prior manual
  // deactivate or the AccountUser cascade) needs a fresh invite -- it has lost its
  // only path back to a user. A still-pending invite (userId set, not yet accepted)
  // keeps the old direct "Activate" behavior.
  const isOrphaned = user.userId == null;
  // #1025: the DELETE /ships/:shipId/users/:userId route (#978's cancel-invite route,
  // repurposed here as the general "remove" action) is registered server-only --
  // no local/app route exists, mirroring AccountUser management already being
  // gated to app.cloudSession() only. So unlike Deactivate/Activate/Reactivate
  // (which reach the shared shipUserResponse() helper via either target), Remove
  // is only offered on a cloud session.
  const canRemove = app.cloudSession();
  const menuItems = [
    // #331: warn before deactivating any ship-user in this (already leveled-only)
    // list -- deactivating can cascade to clear the linked account's ship access.
    ...(user.active > 0 ? [
      { id: "user_inactive", onClick: () => { setPendingAction("deactivate"); return true; }, title: t("user_inactive") },
    ] : []),
    ...(user.active < 1 && !isOrphaned ? [
      { id: "user_active", onClick: () => { updateShipUserActive(true); return true; }, title: t("user_active") },
    ] : []),
    ...(user.active < 1 && isOrphaned ? [
      { id: "user_reactivate", onClick: () => { setShowInviteOverlay(true); return true; }, title: t("user_reactivate") },
    ] : []),
    // #1025: replaces the previously-miswired "opt-out" item (labeled "Remove access" but
    // wired to open InviteUserOverlay for no functional reason) with a real soft-delete,
    // offered regardless of active state (mirrors AccountUser/#979's own "always offers
    // remove" menu) whenever the DELETE route is reachable (see canRemove above).
    ...(canRemove ? [
      MenuItemDividerProps,
      { id: "remove_user", onClick: () => { setPendingAction("remove"); return true; }, title: t("remove_user") },
    ] : []),
  ];

  return (
    <>
      <EditGroup key={user.id} changed={edited} editableFields={userEditableFields} onSave={updateShipUser} className="editgroup_boxed">
        <BlockRow className="ShipUserEditView-1row">
          <BlockField className="block_field_small_22_fix" label={showLabels ? t("user_name") : undefined}>
            <input name="name" value={name} onChange={onEdited} readOnly={!editable} className={user.active < 1 ? "user_deactivated" : ""} />
          </BlockField>
          <BlockField className="block_field_small_12" label={showLabels ? t("user_shortname") : undefined}>
            <input name="shortname" value={shortname} onChange={onEdited} readOnly={!editable} className={user.active < 1 ? "user_deactivated" : ""} />
          </BlockField>

          <BlockField className="block_field_small_10 status_field_desktop" label={showLabels ? t("user_status") : undefined}>
            <input name="user_status" defaultValue={t(`user_status_${status}`)} readOnly={true} className={user.active < 1 ? "user_deactivated" : ""} />
          </BlockField>

          {!isPrimarySelf && menuItems.length > 0 && (
            <BlockField className="button_user_options button_user_options_desktop ship_user_options">
              <Menu button="..." buttonClass="button" global={true} items={menuItems} />
            </BlockField>
          )}
        </BlockRow>

        <BlockRow className="ShipUserEditView-2row">
          <BlockField className="block_field_small_22_fix" label={showLabels ? t("ship_user_level") : undefined}>
            {isPrimarySelf ? (
              // A primary can never demote themselves — the server rejects it
              // (shipUserSelfAction), so the field is shown read-only (#1215).
              <input className={user.active < 1 ? "user_deactivated" : ""} value={ShipDataViewData.userLevelText(t, user.shipUserLevel)} readOnly />
            ) : (
              <select className={user.active < 1 ? "user_deactivated" : ""} defaultValue={user.shipUserLevel} name="shipUserLevel" onChange={onEdited} disabled={!editable}>
                {shipUserLevelOptions}
              </select>
            )}
          </BlockField>
          <BlockField className="block_field_small_12" label={showLabels ? t("user_crew_role") : undefined}>
            {user.shipUserCrewRole === IShipUserCrewRole.owner ? (
              <input
                className={user.active < 1 ? "user_deactivated" : ""}
                name="shipUserCrewRole"
                value={ShipDataViewData.crewRoleText(t, user.shipUserCrewRole)}
                readOnly
              />
            ) : (
              <select className={user.active < 1 ? "user_deactivated" : ""} value={crewRole} name="shipUserCrewRole" onChange={handleCrewRoleChange} disabled={!editable}>
                {shipUserCrewRoleOptions}
                {ShipDataViewData.legacyCrewRoleOption(t, user.shipUserCrewRole)}
              </select>
            )}
          </BlockField>

          <BlockField className="block_field_small_10 status_field_mobile" label={showLabels ? t("user_status") : undefined}>
            <input name="user_status_mobile" defaultValue={t(`user_status_${status}`)} readOnly={true} className={user.active < 1 ? "user_deactivated" : ""} />
          </BlockField>

          {!isPrimarySelf && menuItems.length > 0 && (
            <BlockField className="button_user_options button_user_options_mobile ship_user_options">
              <Menu button="..." buttonClass="button" global={true} items={menuItems} />
            </BlockField>
          )}
        </BlockRow>
      </EditGroup>

      {showInviteOverlay && (
        <InviteUserOverlay
          ship={ship}
          user={user}
          editable={editable}
          shipUserCrewRoleOptions={shipUserCrewRoleOptions}
          shipUserLevelOptions={shipUserLevelOptions}
          onClose={() => setShowInviteOverlay(false)}
          t={t}
        />
      )}

      {pendingAction && (
        <ConfirmDialog
          awaitConfirm={true}
          title={t(`user_${pendingAction}_confirm_title`)}
          message={t(`user_${pendingAction}_confirm_message`)}
          confirmLabel={t(`user_${pendingAction}_confirm_accept`)}
          cancelLabel={t(`user_${pendingAction}_confirm_cancel`)}
          style="alarm"
          onConfirm={confirmPendingAction}
          onCancel={cancelPendingAction}
        />
      )}
    </>
  );
}
